import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Eye, EyeOff, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { PhoneInput, PhoneValue, DEFAULT_PHONE, parsePhoneString, formatPhoneString } from '@/components/ui/PhoneInput';
import { DateInput } from '@/components/ui/DateInput';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { getCountryNames, getStateNames, getCityNames, countryIdByName, stateIdByName, cityIdByName } from '@/lib/geoApi';
import { SelectField } from '@/components/ui/SelectField';

// Country / state / city all come from our own DB master via lib/geoApi.
const fetchStates = getStateNames;
const fetchCities = getCityNames;

// ─────────────────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = ['admin', 'manager', 'counselor', 'agent'];
const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const LANGUAGE_OPTIONS = ['English', 'Hindi', 'Marathi', 'Tamil', 'Telugu', 'Gujarati', 'Bengali', 'Other'];

interface Manager { id: string; name: string; role: string; }
interface NamedItem { id: string; name: string; isActive: boolean; }

interface Props {
  user?: any;
  onClose: () => void;
  onSaved: () => void;
  onPlanLimit?: (message: string) => void;
}

const selectClass = 'flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export function UserFormDrawer({ user, onClose, onSaved, onPlanLimit }: Props) {
  const isEdit = !!user;
  const [managers, setManagers] = useState<Manager[]>([]);
  const [designations, setDesignations] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState<PhoneValue>(() => parsePhoneString(user?.phone || ''));

  // Geo state
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const statesAbort = useRef<AbortController | null>(null);
  const citiesAbort = useRef<AbortController | null>(null);

  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    gender: user?.gender || '',
    dob: user?.dob ? user.dob.split('T')[0] : '',
    email: user?.email || '',
    password: '',
    designation: user?.designation || '',
    department: user?.department || '',
    role: user?.role || 'agent',
    reportingManagerId: user?.reportingManagerId || '',
    dateOfJoining: user?.dateOfJoining ? user.dateOfJoining.split('T')[0] : '',
    language: user?.language || '',
    address: user?.address || '',
    country: user?.country || '',
    state: user?.state || '',
    city: user?.city || '',
    pincode: user?.pincode || '',
    isActive: user?.isActive ?? true,
  });

  const set = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }));

  // Load reference data on mount
  useEffect(() => {
    api.get('/users/managers').then(r => setManagers(r.data)).catch(() => {});
    api.get('/settings/designations').then(r => {
      const items: NamedItem[] = r.data;
      setDesignations(items.filter(d => d.isActive).map(d => d.name));
    }).catch(() => {});
    api.get('/settings/departments').then(r => {
      const items: NamedItem[] = r.data;
      setDepartments(items.filter(d => d.isActive).map(d => d.name));
    }).catch(() => {});

    // Countries from the DB geo master.
    setLoadingCountries(true);
    getCountryNames().then(setCountries).catch(() => {}).finally(() => setLoadingCountries(false));
  }, []);

  // States on country change
  useEffect(() => {
    if (!form.country) { setStates([]); setCities([]); return; }
    statesAbort.current?.abort();
    statesAbort.current = new AbortController();
    setLoadingStates(true);
    setStates([]); setCities([]);
    set('state', ''); set('city', '');
    fetchStates(form.country).then(setStates).catch(() => {}).finally(() => setLoadingStates(false));
  }, [form.country]);

  // Cities on state change
  useEffect(() => {
    if (!form.country || !form.state) { setCities([]); return; }
    citiesAbort.current?.abort();
    citiesAbort.current = new AbortController();
    setLoadingCities(true);
    setCities([]); set('city', '');
    fetchCities(form.country, form.state).then(setCities).catch(() => {}).finally(() => setLoadingCities(false));
  }, [form.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    try {
      const payload: any = {
        ...form,
        phone: formatPhoneString(phone),
        name: `${form.firstName} ${form.lastName}`.trim() || form.email,
        reportingManagerId: form.reportingManagerId || undefined,
        countryId: countryIdByName(form.country) ?? undefined,
        stateId: stateIdByName(form.country, form.state) ?? undefined,
        cityId: cityIdByName(form.country, form.state, form.city) ?? undefined,
      };
      if (isEdit) {
        // Password is a separate flow — never send it in PATCH
        delete payload.password;
      } else {
        // For new users: use entered password or fall back to "Welcome@123"
        if (!payload.password) payload.password = 'Welcome@123';
      }
      if (isEdit) {
        await api.patch(`/users/${user.id}`, payload);
        toast('success', 'User updated successfully');
      } else {
        await api.post('/users', payload);
        toast('success', 'User created successfully' + (!form.password ? ' · Password: Welcome@123' : ''));
      }
      onSaved();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      const errStr = Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to save user';
      // Check for plan limit error (403 with "upgrade" in message)
      if (err?.response?.status === 403 && errStr.toLowerCase().includes('upgrade')) {
        onPlanLimit?.(errStr);
        onClose();
        return;
      }
      setError(errStr);
      toast('error', errStr);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />

      <div className="w-full sm:w-[540px] sm:max-w-none flex-shrink-0 bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit User' : 'New User'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Personal Information */}
          <section className="space-y-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Personal Information</h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>First Name <span className="text-red-500">*</span></Label>
                <Input placeholder="John" value={form.firstName} onChange={e => set('firstName', e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Last Name</Label>
                <Input placeholder="Doe" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Gender</Label>
                <SelectField className={selectClass} value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="">Select gender</option>
                  {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                </SelectField>
              </div>
              <div className="space-y-1.5">
                <Label>Date of Birth</Label>
                <DateInput value={form.dob} onChange={v => set('dob', v)} />
              </div>
            </div>
          </section>

          {/* Account Information */}
          <section className="space-y-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Account Information</h3>

            <div className="space-y-1.5">
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input
                type="email" placeholder="john@company.com"
                value={form.email} onChange={e => set('email', e.target.value)}
                required
              />
            </div>

            {!isEdit && (
              <div className="space-y-1.5">
                <Label>Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Leave blank to use Welcome@123"
                    value={form.password} onChange={e => set('password', e.target.value)}
                    className="pr-10"
                  />
                  <button type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(v => !v)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {!form.password && (
                  <p className="flex items-center gap-1.5 text-xs text-amber-600 mt-1">
                    <Info className="h-3 w-3 flex-shrink-0" />
                    Default password <strong>Welcome@123</strong> will be set
                  </p>
                )}
              </div>
            )}
          </section>

          {/* Work & Locale */}
          <section className="space-y-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Work & Locale</h3>

            <div className="space-y-1.5">
              <Label>Phone</Label>
              <PhoneInput value={phone} onChange={setPhone} placeholder="Phone number" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Designation</Label>
                {designations.length > 0 ? (
                  <SearchableSelect
                    options={designations}
                    value={form.designation}
                    onChange={v => set('designation', v)}
                    placeholder="Select designation"
                  />
                ) : (
                  <Input placeholder="e.g. Sales Executive" value={form.designation} onChange={e => set('designation', e.target.value)} />
                )}
                {designations.length === 0 && (
                  <p className="text-xs text-slate-400 mt-0.5">No designations added · <a href="/settings" className="text-indigo-500 hover:underline">Add in Settings</a></p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Department</Label>
                {departments.length > 0 ? (
                  <SearchableSelect
                    options={departments}
                    value={form.department}
                    onChange={v => set('department', v)}
                    placeholder="Select department"
                  />
                ) : (
                  <Input placeholder="e.g. Sales" value={form.department} onChange={e => set('department', e.target.value)} />
                )}
                {departments.length === 0 && (
                  <p className="text-xs text-slate-400 mt-0.5">No departments added · <a href="/settings" className="text-indigo-500 hover:underline">Add in Settings</a></p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Role <span className="text-red-500">*</span></Label>
                <SelectField className={selectClass} value={form.role} onChange={e => set('role', e.target.value)} required>
                  {ROLE_OPTIONS.map(r => <option key={r} value={r} className="capitalize">{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </SelectField>
              </div>
              <div className="space-y-1.5">
                <Label>Reporting Manager</Label>
                <SelectField className={selectClass} value={form.reportingManagerId} onChange={e => set('reportingManagerId', e.target.value)}>
                  <option value="">None</option>
                  {managers.filter(m => m.id !== user?.id).map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                  ))}
                </SelectField>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date of Joining</Label>
                <DateInput value={form.dateOfJoining} onChange={v => set('dateOfJoining', v)} />
              </div>
              <div className="space-y-1.5">
                <Label>Language</Label>
                <SelectField className={selectClass} value={form.language} onChange={e => set('language', e.target.value)}>
                  <option value="">Select language</option>
                  {LANGUAGE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                </SelectField>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input placeholder="Street address" value={form.address} onChange={e => set('address', e.target.value)} />
            </div>

            {/* Country / State / City */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Country</Label>
                <SearchableSelect
                  options={countries}
                  value={form.country}
                  onChange={v => set('country', v)}
                  placeholder="Select country"
                  loading={loadingCountries}
                />
              </div>
              <div className="space-y-1.5">
                <Label>State {loadingStates && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}</Label>
                <SearchableSelect
                  options={states}
                  value={form.state}
                  onChange={v => set('state', v)}
                  placeholder={!form.country ? 'Select country first' : loadingStates ? 'Loading…' : states.length === 0 ? 'N/A' : 'Select state'}
                  disabled={!form.country || loadingStates}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>City {loadingCities && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}</Label>
                {cities.length > 0 ? (
                  <SearchableSelect
                    options={cities}
                    value={form.city}
                    onChange={v => set('city', v)}
                    placeholder={loadingCities ? 'Loading…' : 'Select city'}
                    disabled={loadingCities}
                  />
                ) : (
                  <Input
                    placeholder={!form.state ? 'Select state first' : 'Type city name'}
                    value={form.city}
                    onChange={e => set('city', e.target.value)}
                    disabled={!form.state}
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Zipcode</Label>
                <Input placeholder="400001" value={form.pincode} onChange={e => set('pincode', e.target.value)} />
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
              <div>
                <p className="text-sm font-medium">Account Status</p>
                <p className="text-xs text-muted-foreground">User can login when active</p>
              </div>
              <button type="button" onClick={() => set('isActive', !form.isActive)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${form.isActive ? 'bg-blue-600' : 'bg-slate-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </section>

          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 border border-red-200">{error}</div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0 bg-white">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit as any} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create User'}
          </Button>
        </div>
      </div>
    </div>
  );
}
